import {
  decodePage,
  isRecord,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireString,
} from '@/lib/api'
import type { PagedResponse } from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

type Schemas = components['schemas']

export type MyNotification = Schemas['MyNotificationDto']

function decodeMyNotification(raw: unknown): MyNotification {
  if (!isRecord(raw)) throw new Error('Malformed notification')
  return {
    notificationId: requireString(raw.notificationId, 'notificationId'),
    subject: nullableString(raw.subject, 'subject'),
    body: requireString(raw.body, 'body'),
    category: requireString(raw.category, 'category'),
    createdAt: requireString(raw.createdAt, 'createdAt'),
    isRead: requireBoolean(raw.isRead, 'isRead'),
  } satisfies MyNotification
}

export type UnreadNotificationCount = Schemas['UnreadCountResponse']

function decodeUnreadNotificationCount(raw: unknown): UnreadNotificationCount {
  if (!isRecord(raw) || typeof raw.unreadCount !== 'number')
    throw new Error('Malformed unread-count response')
  return { unreadCount: raw.unreadCount } satisfies UnreadNotificationCount
}

export type DevBroadcastResult = { recipientCount: number }

function decodeDevBroadcastResult(raw: unknown): DevBroadcastResult {
  if (!isRecord(raw) || typeof raw.recipientCount !== 'number')
    throw new Error('Malformed broadcast response')
  return { recipientCount: raw.recipientCount } satisfies DevBroadcastResult
}

const decodeMyNotificationPage = (
  raw: unknown,
): PagedResponse<MyNotification> =>
  decodePage(raw, decodeMyNotification, 'notifications response')

export const notificationsApi = {
  getMyNotifications: () => request('/api/v1/me/notifications', decodeMyNotificationPage),
  markAllNotificationsRead: () => requestNoContent('/api/v1/me/notifications/read', { method: 'POST' }),
  getUnreadNotificationCount: () => request('/api/v1/me/notifications/unread-count', decodeUnreadNotificationCount),
  devBroadcastNotification: (subject: string, body: string) =>
    request('/api/v1/dev/notifications/broadcast', decodeDevBroadcastResult, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, body }),
    }),
}
